/**
 * AtlasCountryPage — Sprint 4B · Page 2 (country drill-down)
 * Fits to the country polygon (tile-less SVG) and dots its catalogue cities:
 * visited (filled terracotta) vs to-visit (hollow). Tap a visited city → Page 3.
 * Read-only.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildCountryView } from '../../utils/atlasMap';
import theme from '../../styles/theme';

export default function AtlasCountryPage({ country, onSelectCity }) {
  const { t } = useTranslation('common');
  const [view, setView] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    buildCountryView(country).then(v => { if (!cancelled) setView(v); }).catch(() => {});
    return () => { cancelled = true; };
  }, [country]);

  if (!view) return <div style={S.loading}>…</div>;

  return (
    <div style={S.wrap}>
      <h2 style={S.title}>{country}</h2>
      <svg viewBox={view.viewBox} style={S.svg} preserveAspectRatio="xMidYMid meet">
        <path d={view.countryD} fill={theme.bluePale} stroke={theme.blueDeep} strokeWidth={1} strokeOpacity={0.6} />
        {view.cities.map((c, i) => (
          <g key={i} style={{ cursor: c.visited ? 'pointer' : 'default' }}
            onClick={c.visited ? () => onSelectCity(c.city) : undefined}>
            <circle
              cx={c.x} cy={c.y} r={c.visited ? 7 : 5}
              fill={c.visited ? theme.terracotta : 'transparent'}
              stroke={c.visited ? theme.terracottaDark : theme.brown}
              strokeWidth={c.visited ? 0 : 1.5}
              strokeOpacity={c.visited ? 1 : 0.6}
            />
            <text x={c.x} y={c.y - 10} fontSize={13} textAnchor="middle"
              fill={c.visited ? theme.brownDark : theme.sand}
              fontWeight={c.visited ? 700 : 400}>{c.city}</text>
          </g>
        ))}
      </svg>
      <div style={S.legend}>
        <span style={S.legendItem}><span style={{ ...S.dot, background: theme.terracotta }} /> {t('atlasBook.visited')}</span>
        <span style={S.legendItem}><span style={{ ...S.dot, background: 'transparent', border: `1.5px solid ${theme.brown}` }} /> {t('atlasBook.toVisit')}</span>
      </div>
    </div>
  );
}

const S = {
  wrap: { width: '100%' },
  loading: { textAlign: 'center', color: theme.sand, padding: '60px 0', fontSize: 14 },
  title: { textAlign: 'center', margin: '0 0 10px', fontSize: 22, fontWeight: 800, color: theme.brownDark, fontFamily: "'Noto Serif', serif" },
  svg: {
    width: '100%', height: 'auto', maxHeight: '54vh', display: 'block',
    background: theme.cream, borderRadius: 14, border: `1px solid ${theme.creamDeep}`,
  },
  legend: { display: 'flex', justifyContent: 'center', gap: 18, marginTop: 12 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.brown },
  dot: { width: 12, height: 12, borderRadius: '50%', display: 'inline-block' },
};
